import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule, FormGroup, FormControl, Validators, FormBuilder } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { passTemplate as PassInterface } from '../../interfaces/passTemplate';
import { InputComponent } from '../input/input.component';
import { ButtonsComponent } from '../buttons/buttons.component';
import { ButtonComponent } from '../button/button.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import * as templatePasswordConstants from '../../shared/constants/templatepasswordconstants';
import { CommonModule } from '@angular/common';

// Function to validate password based on all criteria
function validatePassword(control: FormControl): { [key: string]: any } | null {
  const pswd = control.value;
  const errors: { [key: string]: boolean } = {}; // Declare the type of errors

  // Check password length
  if (pswd.length < 8 || pswd.length > 32) {
    errors['passwordLength'] = true;
  }

  // Check for uppercase character
  let hasUpper = false;
  for (let i = 0; i < pswd.length; i++) {
    if (pswd.charCodeAt(i) >= 65 && pswd.charCodeAt(i) <= 90) {
      hasUpper = true;
      break;
    }
  }
  if (!hasUpper) {
    errors['uppercase'] = true;
  }

  // Check for number
  let hasNumber = false;
  for (let i = 0; i < pswd.length; i++) {
    if (pswd.charCodeAt(i) >= 48 && pswd.charCodeAt(i) <= 57) {
      hasNumber = true;
      break;
    }
  }
  if (!hasNumber) {
    errors['number'] = true;
  }

  // Check for special character
  let hasSpecialChar = false;
  for (let i = 0; i < pswd.length; i++) {
    if (
      (pswd.charCodeAt(i) >= 33 && pswd.charCodeAt(i) <= 47) ||
      (pswd.charCodeAt(i) >= 58 && pswd.charCodeAt(i) <= 64) ||
      (pswd.charCodeAt(i) >= 91 && pswd.charCodeAt(i) <= 96) ||
      (pswd.charCodeAt(i) >= 123 && pswd.charCodeAt(i) <= 126)
    ) {
      hasSpecialChar = true;
      break;
    }
  }
  if (!hasSpecialChar) {
    errors['specialChar'] = true;
  }

  // Return errors if any, otherwise return null
  return Object.keys(errors).length > 0 ? errors : null;
}

@Component({
  selector: 'app-template-password',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    ButtonsComponent,
    ButtonComponent,
    MatFormFieldModule,
    CommonModule,
  ],
  templateUrl: './template-password.component.html',
  styleUrl: './template-password.component.css',
})
export class TemplatePasswordComponent {
  @Input() title!: PassInterface['title'];
  @Input() desc!: PassInterface['desc'];
  @Input() showExistingPassword!: PassInterface['showExistingPassword'];
  @Input() showSubmitButton!: PassInterface['showSubmitButton'];
  @Input() showSaveButton!: PassInterface['showSaveButton'];
  @Input() showCancelButton!: PassInterface['showCancelButton'];

  @Output() save = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<any>();
  @Output() submit = new EventEmitter<any>();
  @Output() onSubmit = new EventEmitter<void>();
  @Output() newPasswordSubmitted = new EventEmitter<string>();

  constructor(public fb: FormBuilder) {}

  changePasswordForm!: FormGroup;

  isChangePasswordFormValid = false;

  ngOnInit(): void {
    this.changePasswordForm = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, validatePassword]],
      confirmPassword: ['', [Validators.required, validatePassword]],
    });
    this.changePasswordForm = this.fb.group({
      oldPassword: this.oldPassword,
      newPassword: this.newPassword,
      confirmPassword: this.confirmPassword,
    });
  }

  textInputType = templatePasswordConstants.TEXT_INPUT_TYPE;
  passwordInputType = templatePasswordConstants.PASSWORD_INPUT_TYPE;
  lockIcon = templatePasswordConstants.LOCK_ICON;
  visibilityIcon = templatePasswordConstants.VISIBILITY_ICON;
  visibilityOffIcon = templatePasswordConstants.VISIBILITY_OFF_ICON;

  oldPassword = new FormControl('', [Validators.required]);
  oldPswdVisibility = templatePasswordConstants.OLD_PSWD_VISIBILITY;
  oldPasswordLabel = templatePasswordConstants.OLD_PASSWORD_LABEL;
  handleOldPswdVisibilityCallback(event: any) {
    this.oldPswdVisibility = !this.oldPswdVisibility;
  }

  newPassword = new FormControl('', [Validators.required, validatePassword]);
  newPswdVisibility = templatePasswordConstants.NEW_PSWD_VISIBILITY;
  newPasswordLabel = templatePasswordConstants.NEW_PASSWORD_LABEL;

  handleNewPswdVisibilityCallback(event: any) {
    this.newPswdVisibility = !this.newPswdVisibility;
  }

  confirmPassword = new FormControl('', [Validators.required, Validators.maxLength(32), validatePassword]);
  confirmPswdVisibility = templatePasswordConstants.CONFIRM_PSWD_VISIBILITY;
  confirmPasswordLabel = templatePasswordConstants.CONFIRM_PASSWORD_LABEL;

  handleConfirmPswdVisibilityCallback(event: any) {
    this.confirmPswdVisibility = !this.confirmPswdVisibility;
  }

  handleChange() {
    if (this.showExistingPassword) {
      this.isChangePasswordFormValid =
        this.validatePasswordMatch() &&
        validatePassword(this.newPassword) === null &&
        validatePassword(this.confirmPassword) === null &&
        this.oldPassword.valid;
    } else {
      this.isChangePasswordFormValid =
        validatePassword(this.newPassword) === null &&
        validatePassword(this.confirmPassword) === null &&
        this.validatePasswordMatch();
    }
  }

  validatePasswordMatch() {
    if (
      this.newPassword?.value?.length &&
      this.confirmPassword?.value?.length &&
      this.newPassword?.value === this.confirmPassword?.value
    ) {
      return true;
    }
    return false;
  }

  //checks

  hasUpperCase(pswd: string) {
    return /[A-Z]/.test(pswd);
  }

  hasNumber(pswd: string) {
    return /\d/.test(pswd);
  }

  hasSplChar(pswd: string) {
    return /[^a-zA-Z0-9]/.test(pswd);
  }

  handleSave(event: any) {
    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    const existPassword = this.changePasswordForm.get('oldPassword')?.value;
    this.save.emit({ event, newPassword, existPassword }); // Emit the save event
  }

  handleCancel(event: any) {
    this.cancel.emit(event); // Emit the cancel event
  }
  handleSubmit(event: any) {
    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    this.newPasswordSubmitted.emit(newPassword);
    this.onSubmit.emit();
  }

  buttonCancel = templatePasswordConstants.BUTTON_CANCEL;
  arialLabel = templatePasswordConstants.ARIAL_LABEL;
  btnMat = templatePasswordConstants.BTN_MAT;
  btnStyleText = templatePasswordConstants.BTN_STYLE_TEXT;

  buttonSave = templatePasswordConstants.BUTTON_SAVE;
  buttonSubmit = templatePasswordConstants.BUTTON_SUBMIT;
  btnMatFlat = templatePasswordConstants.BTN_MAT_FLAT;
  btnStyleFilledSmall = templatePasswordConstants.BTN_STYLE_FILLED_SMALL;
  btnStyleFilledSmallDisabled = templatePasswordConstants.BTN_STYLE_FILLED_SMALL_DISABLED;
}
